import { Modal, TextInput, Stack, Select, Button, SimpleGrid, Flex } from '@mantine/core';
import { PasswordValidationMessage } from '@common/constants/password';
import { useAuthStore } from '@common/store/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import * as z from 'zod';
import { useAddNewAdminUser } from '@pages/Admin/RpgUsers/hooks/useAddNewAdminUser';
import { ModalFooter } from '@common/mantine/components/modal-footer';
import { AxiosError } from 'axios';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onClick?: () => void;
  children?: React.ReactNode;
};

const schema = z.object({
  token: z.string().or(z.undefined()).nullable(),
  username: z.string().email(),
  password: z.string().min(8, PasswordValidationMessage.Length).max(50),
  first_name: z.string(),
  last_name: z.string(),
  is_sales: z.boolean().optional(),
  sub_account_type: z.number().or(z.string()).nullable().optional()
});

export const AddNewAdminUserModal = ({ isOpen, onClose }: ModalProps) => {
  const { token } = useAuthStore();
  const { createAdminUser } = useAddNewAdminUser();

  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid },
    setError
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      token,
      is_sales: false
    }
  });

  const onSubmit = async (data: z.infer<typeof schema>) => {
    if (!data.token) return;

    try {
      await createAdminUser({
        token: data.token,
        username: data.username,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        is_sales: data.is_sales ?? false
      });
      onClose();
      reset();
    } catch (error) {
      const axiosError = error as AxiosError<Record<string, string | string[]>>;
      if (axiosError.response?.data) {
        Object.entries(axiosError.response.data).forEach(([field, messages]) => {
          setError(field as keyof z.infer<typeof schema>, {
            type: 'manual',
            message: Array.isArray(messages) ? messages.join(' ') : messages
          });
        });
      }
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={() => {
        onClose();
        reset();
      }}
      title="Add New Admin User"
      size="xl"
      styles={{ body: { padding: 0 } }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack p="md">
          <SimpleGrid
            cols={{
              base: 1,
              sm: 2
            }}
          >
            <Controller
              name="first_name"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextInput
                  label="First Name"
                  required
                  variant="filled"
                  placeholder="Please enter first name"
                  value={value}
                  onChange={onChange}
                  error={error?.message}
                />
              )}
            />

            <Controller
              name="last_name"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextInput
                  label="Last Name"
                  required
                  variant="filled"
                  placeholder="Please enter last name"
                  value={value}
                  onChange={onChange}
                  error={error?.message}
                />
              )}
            />

            <Flex align="flex-end">
              <Controller
                name="username"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextInput
                    w="100%"
                    label="Email"
                    required
                    variant="filled"
                    placeholder="Please enter email"
                    value={value}
                    onChange={onChange}
                    error={error?.message}
                    type="email"
                  />
                )}
              />
            </Flex>

            <Controller
              name="password"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextInput
                  label="Password"
                  variant="filled"
                  placeholder="Please enter password"
                  required
                  value={value}
                  onChange={onChange}
                  error={error?.message}
                  description="Minimum 8 characters"
                />
              )}
            />
          </SimpleGrid>
          <Controller
            name="sub_account_type"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Select
                label="Team"
                variant="filled"
                placeholder="Select Team"
                value={value?.toString()}
                onChange={(val) => onChange(val ? parseInt(val) : null)}
                error={error?.message}
                data={[
                  { label: 'Not Selected', value: '' },
                  { label: 'Sales', value: '1' },
                  { label: 'Support', value: '3' }
                ]}
              />
            )}
          />
        </Stack>
        <ModalFooter>
          <Button
            variant="subtle"
            type="button"
            onClick={() => {
              onClose();
              reset();
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={!isValid}>
            Save
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
